import type { ReactNode, Ref } from 'react';
import { Menu, MenuItem, MenuSection } from './Menu.js';
import './AvatarMenu.css';

export interface AvatarMenuGroup {
  /** Uppercase eyebrow — "Your account", "Platform settings". */
  label?: string;
  items: {
    id: string;
    label: string;
    icon?: ReactNode;
    /** A `MENU_GLYPHS` character — the prototype's mono glyph column. */
    glyph?: string;
    hint?: ReactNode;
    href?: string;
    onSelect?: () => void;
    tone?: 'default' | 'danger';
  }[];
}

export interface AvatarMenuProps {
  /** Full name — also the source of the initials. */
  name: string;
  email?: string;
  /** e.g. "Owner · Acme Corp". Rendered as a link to org settings when `roleHref` is given. */
  role?: string;
  roleHref?: string;
  groups: AvatarMenuGroup[];
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * The account menu (design-system catalogue, Tier 2) — the topbar avatar and
 * everything behind it.
 *
 * Absent from the build entirely before this (audit S-6), which meant the product
 * had **no route to profile, notification preferences, keyboard shortcuts, org and
 * locale, safety defaults, AI providers, people and roles, the audit log, billing,
 * or log out** — nine destinations the prototype reaches from here. That is the
 * clearest single case of the shell being incomplete rather than merely mis-styled.
 *
 * Structure, not just styling: the identity block at the top answers "who am I
 * signed in as, in which org, with what role" before any action — which for a
 * product that mutates a customer's CRM is the question worth answering first.
 * `groups` is a list rather than fixed props so a caller can render only the
 * sections its plan and role actually permit; the component does not decide
 * entitlement.
 */
export function AvatarMenu({ name, email, role, roleHref, groups }: AvatarMenuProps) {
  const initials = initialsOf(name);
  return (
    <Menu
      label="Account and settings"
      align="end"
      width={288}
      trigger={({ ref, ...triggerProps }) => (
        <button
          {...triggerProps}
          ref={ref as Ref<HTMLButtonElement>}
          type="button"
          className="dpr-avatar-menu__trigger"
          aria-label={`Account: ${name}${role ? `, ${role}` : ''}`}
        >
          <span aria-hidden="true">{initials}</span>
        </button>
      )}
    >
      <div className="dpr-avatar-menu__identity">
        <span className="dpr-avatar-menu__identity-avatar" aria-hidden="true">
          {initials}
        </span>
        <span className="dpr-avatar-menu__identity-text">
          <span className="dpr-avatar-menu__name">{name}</span>
          {email ? <span className="dpr-avatar-menu__email">{email}</span> : null}
          {role ? (
            roleHref ? (
              <a className="dpr-avatar-menu__role" href={roleHref}>
                {role}
              </a>
            ) : (
              <span className="dpr-avatar-menu__role">{role}</span>
            )
          ) : null}
        </span>
      </div>

      {groups.map((group, i) => (
        <MenuSection key={group.label ?? i} label={group.label}>
          {group.items.map((item) => (
            <MenuItem
              key={item.id}
              icon={item.icon}
              glyph={item.glyph}
              hint={item.hint}
              href={item.href}
              onSelect={item.onSelect}
              tone={item.tone}
            >
              {item.label}
            </MenuItem>
          ))}
        </MenuSection>
      ))}
    </Menu>
  );
}
